import { CREATE, DELETE, FETCH, FULFILLED, REJECTED, UPDATE } from 'actionTypes';
import arrayToObject from 'utils/arrayToObject';
import pluralize from 'utils/pluralize';
import dataNormalizer from './optionsBaseDataTransform';

/**
 * A collection is `models` - the rows by id - and `ids` - which rows there are,
 * in the order the API sent them. `data` is the array, rebuilt from those two
 * whenever the slice changes.
 *
 * It used to be the other way round. `data` and `dataById` were both stored and
 * both authoritative, kept in step by hand, and they did not stay in step: the
 * delete case cleaned `data` and left `dataById` holding every row deleted in
 * the session. Nothing noticed, because every screen read `data`.
 *
 * They were not even the same rows - FETCH put `payload` in one and
 * `cloneDeep(payload)` in the other - so every collection sat in memory twice
 * and every fetch deep-cloned it. Raw materials alone was 692 KB per boot.
 *
 * Now there is one copy. `data` holds the same row objects `models` does, which
 * is what removed the clone, and is also why the two tables that handed a raw
 * slice to material-table had to start cloning like every other screen already
 * did: material-table writes onto the rows it is given, and there is no longer
 * a spare copy to absorb it.
 *
 * `data` stays for now because eighty-odd screens read it. They move to `ids`
 * and `models` as each one is opened for the payload work; when the last goes,
 * so does this line.
 *
 * Two places where this deliberately does not match what the old code did.
 * Neither can be reached from the app's own flows - both were found by reading,
 * not by running - but they are decisions, so they are written down.
 *
 * **An update for a row the list does not hold now adds it.** The old code
 * looked the row up in `data`, and did nothing at all when it was not there
 * while still writing it into `dataById` - the same drift as everywhere else.
 * One list cannot do both, and a row the server has just confirmed belongs in
 * the list. Every UPDATE the app fires comes from a row on screen, so the
 * branch is dead either way.
 *
 * **`data` is only as faithful as the ids are unique.** It is rebuilt by
 * looking each id up in `models`, so two rows sharing an id would show the
 * second one twice and lose the first. `data = cloneDeep(payload)` could not do
 * that. This is not hypothetical in shape - reverse relations in this API do
 * arrive with repeats - but no collection endpoint does it: 13368 rows across
 * the whole verification dataset, no duplicate id. If one ever appears it is a
 * payload defect, and this shape shows it rather than quietly carrying it.
 */
const indexed = (state, models, ids) => ({
  ...state,
  models,
  ids,
  data: ids.map(id => models[id])
});

// A duck that holds a single object declares an `entity` and no collection, and
// everything below has to leave it alone.
//
// There are none left. Both went the same way and for the same reason: a row of
// a collection the application already fetches is not a second endpoint.
// `userAccount` went when `/accounts/` became scoped, and `profile` when
// `/users/` started serving everybody - what remains of "who am I" is one
// integer in `state.auth`, joined against `state.users` by
// `selectors/currentUser`.
//
// The guard stays because the shape is still expressible and a reducer that
// quietly rewrote such a duck into a collection would be a bad way to find out.
const isCollection = state => Boolean(state && state.models);

export default (TYPE, initialState, options) => {
  const PLURAL = pluralize(TYPE);
  const start = isCollection(initialState)
    ? indexed(initialState, initialState.models, initialState.ids)
    : initialState;

  return (state = start, { meta, payload, type }) => {
    let ids, models;

    switch (type) {

      case FETCH + TYPE:
      case FETCH + PLURAL:
        return { ...state, errorMessage: '', loading: true };

      case CREATE + TYPE:
      case UPDATE + TYPE:
      case DELETE + TYPE:
        return { ...state, errorMessage: '', loading: true, submitting: true };

      case CREATE + TYPE + FULFILLED:
        payload = dataNormalizer(payload, options);
        models = { ...state.models, [payload.id]: payload };
        ids = [...state.ids, payload.id];
        return indexed(
          { ...state, errorMessage: '', loading: false, submitting: false }, models, ids);

      case UPDATE + TYPE + FULFILLED:
        if (!isCollection(state)) {
          return { ...state, entity: payload };
        }
        payload = dataNormalizer(payload, options);
        models = { ...state.models, [payload.id]: payload };
        ids = state.ids.includes(payload.id) ? state.ids : [...state.ids, payload.id];
        return indexed(
          { ...state, errorMessage: '', loading: false, submitting: false }, models, ids);

      case DELETE + TYPE + FULFILLED:
        models = { ...state.models };
        delete models[payload];
        // Both sides compared the same way, because the two lookups are not the
        // same by default: an object key is coerced to a string, so
        // `delete models[payload]` removes the row whether the id arrives as 123
        // or "123", while a strict filter would keep "123" against a numeric 123.
        // The id is a number today - it comes from `rowData.id` out of JSON - so
        // this never fires; it is the consequence if it ever did that is worth
        // closing. The row would leave `models`, stay in `ids`, and `data` would
        // hold `undefined` where a row should be, which throws on the first field
        // read. The old shape could not do that: `data` and `dataById` were
        // independent, so a missed match merely left the row on screen.
        ids = state.ids.filter(id => String(id) !== String(payload));
        return indexed(
          { ...state, errorMessage: '', loading: false, submitting: false }, models, ids);

      case FETCH + TYPE + FULFILLED:
        if (!isCollection(state)) {
          return { ...state, entity: payload };
        }
        payload = dataNormalizer(payload, options);
        models = { ...state.models, [payload.id]: payload };
        ids = state.ids.includes(payload.id) ? state.ids : [...state.ids, payload.id];
        return indexed({ ...state, errorMessage: '', loading: false }, models, ids);

      case FETCH + PLURAL + FULFILLED:
        if (!isCollection(state)) {
          return { ...state, entity: payload };
        }
        payload = dataNormalizer(payload, options);
        models = arrayToObject(payload);
        ids = payload.map(row => row.id);
        return indexed(
          { ...state, errorMessage: '', loaded: true, loading: false }, models, ids);

      case FETCH + PLURAL + REJECTED:
      case FETCH + TYPE + REJECTED:
        return { ...state, loading: false, errorMessage: payload };

      case CREATE + TYPE + REJECTED:
      case UPDATE + TYPE + REJECTED:
      case DELETE + TYPE + REJECTED:
        return { ...state, loading: false, errorMessage: payload, submitting: false };

      default:
        return state;
    }
  };
};
